import { oids } from "@/configs/Settings";

import {
  FLUXZAP_BASIC_BATCH_IDENTIFIER_SYSTEM,
  FLUXZAP_BASIC_NOTIFICATION_IDENTIFIER_SYSTEM,
  FLUXZAP_BASIC_NOTIFICATION_IDENTIFIER_VALUE,
  FLUXZAP_BASIC_ORGANIZATION_IDENTIFIER_SYSTEM,
  FLUXZAP_BASIC_TEMPLATE_CODING_SYSTEM,
} from "../constants/clinicalCrmCommunicationsFhirConstants";
import type { FluxZapCommunicationRecord } from "../types";

export function buildFluxZapOrganizationReference(
  organizationId: string,
): string {
  const trimmed = organizationId.trim();
  const prefix = `${oids.cnpj}-`;
  const orgId = trimmed.startsWith(prefix) ? trimmed : `${prefix}${trimmed}`;
  return `Organization/${orgId}`;
}

function normalizeOrganizationIdentifierValue(
  value: string | undefined,
): string | null {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("Organization/")) return trimmed;

  const prefix = `${oids.cnpj}-`;
  if (trimmed.startsWith(prefix)) {
    return `Organization/${trimmed}`;
  }
  return `Organization/${prefix}${trimmed}`;
}

export function isFluxZapBasicForOrganization(
  basic: fhir5.Basic,
  organizationReference: string,
): boolean {
  const identifiers = basic.identifier ?? [];
  const hasFluxzap = identifiers.some(
    (id) =>
      id.system === FLUXZAP_BASIC_NOTIFICATION_IDENTIFIER_SYSTEM &&
      id.value?.trim() === FLUXZAP_BASIC_NOTIFICATION_IDENTIFIER_VALUE,
  );
  if (!hasFluxzap) return false;

  const expectedOrg = normalizeOrganizationIdentifierValue(organizationReference);
  if (!expectedOrg) return false;

  const orgIdentifier = identifiers.find(
    (id) => id.system === FLUXZAP_BASIC_ORGANIZATION_IDENTIFIER_SYSTEM,
  );

  if (orgIdentifier?.value?.trim()) {
    return (
      normalizeOrganizationIdentifierValue(orgIdentifier.value) === expectedOrg
    );
  }

  // Fallback: author `PractitionerRole/{orgId}-{practitionerId}` quando OrganizationId
  // ainda não foi persistido no Basic pelo backend.
  const orgId = expectedOrg.replace(/^Organization\//, "");
  const authorRef = basic.author?.reference?.trim() ?? "";
  return authorRef.includes(orgId);
}

/** Rótulo amigável derivado do código do template (sem inventar labels do mock). */
export function humanizeTemplateCode(templateCode: string): string {
  const underscore = templateCode.indexOf("_");
  const suffix =
    underscore >= 0 ? templateCode.slice(underscore + 1) : templateCode;
  if (!suffix.trim()) return templateCode;
  return suffix
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function pickTemplateCode(basic: fhir5.Basic): string {
  const coding = basic.code?.coding?.find(
    (c) => c.system === FLUXZAP_BASIC_TEMPLATE_CODING_SYSTEM && c.code,
  );
  return coding?.code?.trim() ?? "";
}

function pickBatchId(basic: fhir5.Basic): string | undefined {
  const batchIdentifier = basic.identifier?.find(
    (id) =>
      id.system === FLUXZAP_BASIC_BATCH_IDENTIFIER_SYSTEM && id.value?.trim(),
  );
  return batchIdentifier?.value?.trim() || undefined;
}

/** Mapeia Basic (notificação FluxZap) para registro de comunicação da UI. */
export function mapFhirBasicToFluxZapCommunicationRecord(
  basic: fhir5.Basic,
): FluxZapCommunicationRecord | null {
  const id = basic.id?.trim();
  const templateCode = pickTemplateCode(basic);
  const subjectRef = basic.subject?.reference?.trim();
  if (!id || !templateCode || !subjectRef) return null;

  return {
    id,
    templateCode,
    templateLabel: humanizeTemplateCode(templateCode),
    subjectPatientReference: subjectRef,
    created: basic.created ?? basic.meta?.lastUpdated ?? "",
    authorPractitionerRoleReference: basic.author?.reference,
    batchId: pickBatchId(basic),
  };
}

export function mapFhirBasicsToFluxZapCommunicationRecords(
  basics: fhir5.Basic[],
): FluxZapCommunicationRecord[] {
  const result: FluxZapCommunicationRecord[] = [];
  for (const basic of basics) {
    const mapped = mapFhirBasicToFluxZapCommunicationRecord(basic);
    if (mapped) result.push(mapped);
  }
  return result;
}
